/**
 * Storage access that never throws: private windows, blocked site data and
 * sandboxed previews can make the accessor itself throw.
 */
type Area = 'local' | 'session';

function area(kind: Area): Storage | null {
  try {
    return kind === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

export function readStorage(kind: Area, key: string): string | null {
  try {
    return area(kind)?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

export function writeStorage(kind: Area, key: string, value: string | null): void {
  try {
    const store = area(kind);
    if (!store) return;
    if (value === null) store.removeItem(key);
    else store.setItem(key, value);
  } catch {
    /* quota or access errors are not fatal for conveniences */
  }
}

export function readJson<T>(kind: Area, key: string, guard: (value: unknown) => value is T): T | null {
  const raw = readStorage(kind, key);
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return guard(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Remove every key starting with `prefix` (per-account conveniences at sign-out). */
export function removeStorageKeys(kind: Area, prefix: string): void {
  try {
    const store = area(kind);
    if (!store) return;
    const keys: string[] = [];
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i);
      if (key?.startsWith(prefix)) keys.push(key);
    }
    for (const key of keys) store.removeItem(key);
  } catch {
    /* storage unavailable: nothing to clear */
  }
}
